import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { createTermin } from '../../../../client/termin/terminCreateClient';
import TerminCreateUpdateType from '../../../../model/TerminCreate';
import { useSuccessMessageContext } from '../../../../context/SuccessMessageContext';
import { useClient, useClientStatusContext } from '../../../../context/ClientStatusContext';
import routes from '../../../../routing/routes';
import useFieldErrorMessages from '../../../useFieldErrorMessages';
import { SafeParameterSetter } from '../../../../model/SafeParameterSetter';
import TerminCreateUpdateForm from './component/TerminCreateUpdateForm';
import { updateTermin } from '../../../../client/termin/terminUpdateClient';
import { technickePricinyErrorStatus } from '../../../../constants/ErrorConstant';

interface Props {
  mode: 'CREATE' | 'UPDATE';
  terminCurrent?: TerminCreateUpdateType;
}

const TerminCreateUpdate = ({ mode, terminCurrent }: Props): ReactElement => {
  const { uuid, tezaurusUuid, terminUuid } = useParams<{ uuid: string, tezaurusUuid: string, terminUuid: string }>();
  const [termin, setTermin] = useState<TerminCreateUpdateType>(terminCurrent || {
    preferovanyTermin: '',
    alternativnyTerminList: new Array<string>(''),
  });
  const { handleStatus } = useClientStatusContext();

  const {
    wrapClient: createTerminClient,
    status: createTerminStatus,
  } = useClient(createTermin);

  const {
    wrapClient: updateTerminClient,
    status: updateTerminStatus,
  } = useClient(updateTermin);

  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
    clearArrayFieldErrorMessage,
  } = useFieldErrorMessages();

  useEffect(() => {
    if (updateTerminStatus?.responseCode === 404 || createTerminStatus?.responseCode === 404) {
      handleStatus(technickePricinyErrorStatus);
    }
  }, [updateTerminStatus, createTerminStatus, handleStatus]);

  const handleSubmit = async (event): Promise<void> => {
    event.preventDefault();

    if (isCreate()) {
      const { data: updatedTerminUuid } = await createTerminClient(uuid, termin);
      navigate(routes.tezaurus.admin.termin.detail.withParams(uuid, updatedTerminUuid));
    } else {
      await updateTerminClient(terminUuid, { uuid: terminUuid, ...termin });
      navigate(routes.tezaurus.admin.termin.detail.withParams(tezaurusUuid, terminUuid));
    }

    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  const updateField: SafeParameterSetter<TerminCreateUpdateType> = (fieldName, value) => {
    setTermin(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
    if (fieldName === 'preferovanyTermin') {
      clearArrayFieldErrorMessage('alternativnyTermin');
    }
  };

  function isCreate(): boolean {
    return mode === 'CREATE';
  }

  return (
    <TerminCreateUpdateForm
      termin={termin}
      loading={createTerminStatus.loading || updateTerminStatus.loading}
      getFieldErrorMessage={getFieldErrorMessage}
      clearArrayFieldErrorMessage={clearArrayFieldErrorMessage}
      clearFieldErrorMessage={clearFieldErrorMessage}
      onUpdateField={updateField}
      onSubmit={handleSubmit}
      mode={mode}
    />
  );
};

export default TerminCreateUpdate;
